import React, { useState } from 'react';
import {Phone, Mail, MapPin, Clock, MessageCircleDashed as MessageCircle, Send} from 'lucide-react';
import { lumi } from '@/lib/lumi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await lumi.functions.invoke('sendContactEmail', {
        method: 'POST',
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          formType: 'contact'
        }
      });

      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you! We\'ve received your message and will respond within 30 minutes during business hours.' 
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to send message. Please try calling us at 07454 922145.';
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#333333] to-[#4682B4] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Get In <span className="text-[#FFD700]">Touch</span>
          </h1>
          <p className="text-xl text-[#E0E0E0] max-w-3xl mx-auto leading-relaxed">
            Get in touch with our team for fast, professional mobile mechanic services across Birmingham, Coventry, Wolverhampton and the West Midlands
          </p>
        </div>
      </section>

      {/* Request a Callback Section */}
      <section className="py-16 bg-gradient-to-br from-[#4682B4] to-[#333333]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="inline-block bg-[#FFD700] p-4 rounded-full mb-4">
                <Phone className="w-8 h-8 text-[#333333]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-3">
                Request a <span className="text-[#4682B4]">Callback</span>
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll call you back within 30 minutes during business hours
              </p>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              setSubmitStatus(null);
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              
              try {
                await lumi.functions.invoke('sendContactEmail', {
                  method: 'POST',
                  body: {
                    name: formData.get('name') as string,
                    phone: formData.get('phone') as string,
                    email: 'callback@onpointmechanics.temp',
                    date: formData.get('date') as string,
                    time: formData.get('time') as string,
                    message: formData.get('message') as string,
                    formType: 'callback'
                  }
                });
                
                setSubmitStatus({ 
                  type: 'success', 
                  message: 'Thank you! We will call you back within 30 minutes during business hours.' 
                });
                form.reset();
              } catch (error: any) {
                const errorMessage = error?.data?.error || 'Failed to submit request. Please call us at 07454 922145.';
                setSubmitStatus({ type: 'error', message: errorMessage });
              } finally {
                setIsSubmitting(false);
              }
            }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="callback-name" className="block text-sm font-bold text-[#333333] mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="callback-name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="callback-phone" className="block text-sm font-bold text-[#333333] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="callback-phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                    placeholder="07454 922145"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="callback-date" className="block text-sm font-bold text-[#333333] mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    id="callback-date"
                    name="date"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="callback-time" className="block text-sm font-bold text-[#333333] mb-2">
                    Preferred Time
                  </label>
                  <select
                    id="callback-time"
                    name="time"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                  >
                    <option value="">Select a time</option>
                    <option value="morning">Morning (8AM - 12PM)</option>
                    <option value="afternoon">Afternoon (12PM - 4PM)</option>
                    <option value="evening">Evening (4PM - 6PM)</option>
                    <option value="asap">As Soon As Possible</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="callback-message" className="block text-sm font-bold text-[#333333] mb-2">
                  What can we help you with? *
                </label>
                <textarea
                  id="callback-message"
                  name="message"
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors resize-none"
                  placeholder="Briefly describe your vehicle issue or service needs..."
                />
              </div>

              {submitStatus && (
                <div className={`p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800 border-2 border-green-500' : 'bg-red-100 text-red-800 border-2 border-red-500'}`}>
                  <p className="font-semibold">{submitStatus.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FFD700] text-[#333333] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#4682B4] hover:text-white transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Phone className="w-6 h-6" />
                <span>{isSubmitting ? 'Sending...' : 'Request Callback Now'}</span>
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Or call us directly at <a href="tel:07454922145" className="text-[#4682B4] font-semibold hover:text-[#FFD700]">07454 922145</a>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-[#333333] mb-6">
                  Contact Information
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Reach out to us for fast, professional mobile mechanic services. We're available to help with all your auto repair needs.
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#4682B4] p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">Phone</h3>
                    <a 
                      href="tel:07454922145" 
                      className="text-[#4682B4] hover:text-[#FFD700] transition-colors text-lg"
                      aria-label="Call ONPOINT Mobile Mechanics"
                    >
                      07454 922145
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Available for calls and texts</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#4682B4] p-3 rounded-lg">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">WhatsApp</h3>
                    <a 
                      href="https://wa.me/447308538351"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4682B4] hover:text-[#FFD700] transition-colors text-lg"
                      aria-label="Message us on WhatsApp"
                    >
                      Send a WhatsApp message
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Quick response guaranteed</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#4682B4] p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">Email</h3>
                    <a 
                      href="mailto:onpointmobilemechanics@gmail.com" 
                      className="text-[#4682B4] hover:text-[#FFD700] transition-colors"
                      aria-label="Email ONPOINT Mobile Mechanics"
                    >
                      onpointmobilemechanics@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#4682B4] p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">Service Areas</h3>
                    <p className="text-gray-600 mb-2">Mobile Service - We Come To You</p>
                    <div className="bg-white p-4 rounded-lg border-2 border-[#4682B4]">
                      <h4 className="font-bold text-[#333333] mb-2 text-sm">Covering the West Midlands:</h4>
                      <div className="grid grid-cols-2 gap-1 text-gray-700 text-sm">
                        <div>• Birmingham</div>
                        <div>• Coventry</div>
                        <div>• Wolverhampton</div>
                        <div>• Dudley</div>
                        <div>• Walsall</div>
                        <div>• Solihull</div>
                        <div>• West Bromwich</div>
                        <div>• Smethwick</div>
                        <div>• Oldbury</div>
                        <div>• Tipton</div>
                        <div>• Halesowen</div>
                        <div>• Stourbridge</div>
                        <div>• Rowley Regis</div>
                        <div>• Bilston</div>
                        <div>• Willenhall</div>
                        <div>• Sutton Coldfield</div>
                        <div>• Kingswinford</div>
                        <div>• Brierley Hill</div>
                        <div>• Aldridge</div>
                        <div>• Brownhills</div>
                        <div>• Sedgley</div>
                        <div>• Coseley</div>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mt-2">Don't see your area? Call us to check coverage!</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#4682B4] p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">Opening Hours</h3>
                    <div className="space-y-1 text-gray-600">
                      <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                      <p>Saturday: 9:00 AM - 4:00 PM</p>
                      <p>Sunday: Emergency Service Only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <a
                  href="tel:07454922145"
                  className="bg-[#FFD700] text-[#333333] px-6 py-3 rounded-lg font-bold hover:bg-[#4682B4] hover:text-white transition-all flex items-center justify-center space-x-2"
                  aria-label="Call now for immediate service"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </a>
                <a
                  href="https://wa.me/447308538351"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#4682B4] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#FFD700] hover:text-[#333333] transition-all flex items-center justify-center space-x-2"
                  aria-label="Start WhatsApp chat"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#E0E0E0] p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-[#333333] mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#333333] mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#333333] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#333333] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors"
                    placeholder="07454 922145"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#333333] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#4682B4] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your vehicle issue or service needs..."
                  />
                </div>

                {submitStatus && (
                  <div className={`p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800 border-2 border-green-500' : 'bg-red-100 text-red-800 border-2 border-red-500'}`}>
                    <p className="font-semibold">{submitStatus.message}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#4682B4] text-white px-6 py-4 rounded-lg font-bold hover:bg-[#FFD700] hover:text-[#333333] transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Send message to ONPOINT Mobile Mechanics"
                >
                  <Send className="w-5 h-5" />
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-[#E0E0E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#333333] mb-4">
              Mobile Service Coverage
            </h2>
            <p className="text-lg text-gray-600">
              We bring our professional services directly to your location
            </p>
          </div>
          
          {/* Placeholder for map - you can integrate Google Maps or similar */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden h-96 flex items-center justify-center">
            <div className="text-center p-8">
              <MapPin className="w-16 h-16 text-[#4682B4] mx-auto mb-4" />
              <p className="text-xl font-semibold text-[#333333] mb-2">We Come To You!</p>
              <p className="text-gray-600">Serving all local areas with mobile mechanic services</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
